import { z } from "zod";

const CREDIT_CARD_REGEX = /^(?:\d{14,19}|\d{4}(?: \d{3,6}){2,4}|\d{4}(?:-\d{3,6}){2,4})$/u;
const SANITIZE_REGEX = /[- ]/gu;
const NON_DIGIT_REGEX = /\D/gu;

const PROVIDER_REGEXES = [
  /^3[47]\d{13}$/u, // American Express
  /^3(?:0[0-5]|[68]\d)\d{11,13}$/u, // Diners Club
  /^6(?:011|5\d{2})\d{12,15}$/u, // Discover
  /^(?:2131|1800|35\d{3})\d{11}$/u, // JCB
  /^5[1-5]\d{2}|(?:222\d|22[3-9]\d|2[3-6]\d{2}|27[01]\d|2720)\d{12}$/u, // Mastercard
  /^(?:6[27]\d{14,17}|81\d{14,17})$/u, // UnionPay
  /^4\d{12}(?:\d{3,6})?$/u, // Visa
];

function passesLuhn(cardNumber: string): boolean {
  const digits = cardNumber.replace(NON_DIGIT_REGEX, "");
  let length = digits.length;
  let bit = 1;
  let sum = 0;
  const doubled = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];
  while (length) {
    const value = Number(digits[--length] ?? 0);
    bit ^= 1;
    sum += bit ? (doubled[value] ?? 0) : value;
  }
  return sum % 10 === 0;
}

export function isValidCreditCard(cardNumber: string): boolean {
  const sanitized = cardNumber.replace(SANITIZE_REGEX, "");
  return (
    CREDIT_CARD_REGEX.test(cardNumber) &&
    sanitized !== "" &&
    PROVIDER_REGEXES.some((regex) => regex.test(sanitized)) &&
    passesLuhn(sanitized)
  );
}

export function sanitizeCardNumber(cardNumber: string): string {
  return cardNumber.replace(SANITIZE_REGEX, "");
}

export function parseExpiry(expDate: string): { expMonth: string; expYear: string } {
  const [expMonth = "", expYear = ""] = expDate.split("/");
  return { expMonth, expYear };
}

/** Longest card number we accept: 19 digits in groups of four. */
export const CARD_NUMBER_MASK = "#### #### #### #### ###";
export const EXPIRY_MASK = "##/####";

export const FEE_NOTICE_NEW_CARD =
  "We impose a surcharge of 3.4% + $0.5 for Debit & Credit Cards only, which goes towards card processing costs and is not greater than our cost of acceptance. Unfortunately, we currently don't accept Visa, but you can proceed with other payment networks.";

export const FEE_NOTICE_SAVED_CARD =
  "We will automatically charge your card on file after the due date of your invoice. A processing fee charged by the credit card processor of 3.4% + 50 cents will be added to the bill. Alternatively, you can manually pay via PayNow directly with the invoice which does not have a processing fee.";

export const cardSchema = z.object({
  name: z.string().trim().min(1, { error: "Cardholder name is required" }),
  number: z.string().superRefine((value, ctx) => {
    // Validate the digits, not the masked string: `formatCardNumber` groups in
    // fours, and a 14-, 17-, or 18-digit card ends in a group too short for
    // CREDIT_CARD_REGEX's `\d{3,6}` separator alternative.
    const sanitized = sanitizeCardNumber(value);
    if (sanitized.startsWith("4")) {
      ctx.addIssue({
        code: "custom",
        message:
          "We're currently unable to accept Visa — please try a different card or PayNow for lower fees.",
      });
      return;
    }
    if (!isValidCreditCard(sanitized)) {
      ctx.addIssue({ code: "custom", message: "Must be a valid credit card number" });
    }
  }),
  expDate: z
    .string()
    .length(7, { error: "Must be a valid format (MM/YYYY)" })
    .refine((value) => {
      const [month, year] = value.split("/").map(Number);
      if (month == null || year == null) return false;
      if (isNaN(month) || isNaN(year) || month < 1 || month > 12) return false;
      const expiry = new Date(year, month - 1, 1);
      const now = new Date();
      return expiry >= new Date(now.getFullYear(), now.getMonth(), 1);
    }, "Must be a valid expiration date"),
  cvc: z
    .string()
    .min(3, { error: "CVC must be at least 3 digits" })
    .max(4, { error: "CVC must be at most 4 digits" }),
});

export type CardFormValues = z.infer<typeof cardSchema>;

export const emptyCardValues: CardFormValues = {
  name: "",
  number: "",
  expDate: "",
  cvc: "",
};
